import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Clock,
  Search,
  Eye,
  Check,
  X,
  MoreHorizontal,
  Filter,
  Download,
  RefreshCw,
  AlertCircle,
  FileText
} from 'lucide-react';
import ExpenseStatusBadge from '../contractor/ExpenseStatusBadge';
import expenseService from '@/services/expenseService';
import {
  Expense,
  ExpenseCategory,
  ExpenseStatus,
  EXPENSE_CATEGORY_INFO,
  ExpenseFilters
} from '@/types/expense';

interface PendingExpenseApprovalsProps {
  contractId?: string; // Filter by specific contract
  onViewExpense?: (expense: Expense) => void;
  onApproveExpense?: (expense: Expense) => void;
  onRejectExpense?: (expense: Expense) => void;
  className?: string;
}

const PendingExpenseApprovals: React.FC<PendingExpenseApprovalsProps> = ({
  contractId,
  onViewExpense,
  onApproveExpense,
  onRejectExpense,
  className
}) => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ExpenseCategory | ''>('');
  const { toast } = useToast();

  const fetchPendingExpenses = async () => {
    try {
      setIsLoading(true);
      
      let response: any;
      
      if (contractId) {
        // Client view: get expenses for specific contract review
        console.log('Fetching pending expenses for contract:', contractId);
        response = await expenseService.getExpensesForClientReview(contractId);
        console.log('Raw API response:', response);
        
        // Filter by search and category on frontend since backend doesn't support these filters
        let filteredExpenses = response.expenses || [];
        console.log('Expenses from API:', filteredExpenses);
        
        if (searchQuery.trim()) {
          const query = searchQuery.trim().toLowerCase();
          filteredExpenses = filteredExpenses.filter((expense: Expense) =>
            expense.description.toLowerCase().includes(query) ||
            expense.id.toLowerCase().includes(query)
          );
          console.log('After search filter:', filteredExpenses);
        }
        
        if (selectedCategory) {
          filteredExpenses = filteredExpenses.filter((expense: Expense) =>
            expense.category === selectedCategory
          );
          console.log('After category filter:', filteredExpenses);
        }
        
        console.log('Final filtered expenses to set:', filteredExpenses);
        setExpenses(filteredExpenses);
      } else {
        // Contractor view: use existing contractor endpoint with filters
        const filters: ExpenseFilters = {
          status: [ExpenseStatus.PENDING_APPROVAL],
          ...(searchQuery.trim() && { search: searchQuery.trim() }),
          ...(selectedCategory && { category: [selectedCategory] }),
        };

        response = await expenseService.getContractorExpenses(filters);
        setExpenses(response.expenses);
      }
    } catch (error: any) {
      console.error('Error fetching pending expenses:', error);
      toast({
        title: 'Error',
        description: 'Failed to load pending expenses',
        variant: 'destructive',
      });
      setExpenses([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingExpenses();
  }, [contractId, searchQuery, selectedCategory]);

  const handleQuickApprove = async (expense: Expense) => {
    try {
      await expenseService.approveOrRejectExpense(expense.id, {
        approved: true,
        notes: 'Approved via quick action'
      });
      
      toast({
        title: 'Success',
        description: `Expense approved: ${expense.description}`,
      });
      
      // Don't call onApproveExpense as that opens the modal - this is a direct approval
      fetchPendingExpenses(); // Refresh the list
    } catch (error: any) {
      console.error('Error approving expense:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve expense',
        variant: 'destructive',
      });
    }
  };

  const handleQuickReject = async (expense: Expense) => {
    try {
      await expenseService.approveOrRejectExpense(expense.id, {
        approved: false,
        rejectionReason: 'Rejected via quick action - please provide more details',
        notes: 'Rejected via quick action'
      });
      
      toast({
        title: 'Success',
        description: `Expense rejected: ${expense.description}`,
      });
      
      // Don't call onRejectExpense as that opens the modal - this is a direct rejection
      fetchPendingExpenses(); // Refresh the list
    } catch (error: any) {
      console.error('Error rejecting expense:', error);
      toast({
        title: 'Error',
        description: 'Failed to reject expense',
        variant: 'destructive',
      });
    }
  };

  const handleViewReceipt = async (expense: Expense) => {
    try {
      await expenseService.downloadReceipt(expense.id);
    } catch (error: any) {
      console.error('Error viewing receipt:', error);
      toast({
        title: 'Error',
        description: 'Failed to view receipt. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const formatAmount = (amount: string) => {
    return expenseService.formatAmount(amount);
  };

  const formatDate = (dateString: string) => {
    return expenseService.formatDate(dateString);
  };

  const getTotalPendingAmount = () => {
    return expenses.reduce((total, expense) => total + parseFloat(expense.amount), 0);
  };

  const getCategoryInfo = (category: ExpenseCategory) => {
    return EXPENSE_CATEGORY_INFO[category];
  };

  // For client view with contractId, filtering is already done in fetchPendingExpenses
  // For contractor view without contractId, we do additional filtering here
  const filteredExpenses = contractId 
    ? expenses // Already filtered in fetchPendingExpenses for client view
    : expenses.filter(expense => {
        const matchesSearch = !searchQuery || 
          expense.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          expense.id.toLowerCase().includes(searchQuery.toLowerCase());
        
        const matchesCategory = !selectedCategory || expense.category === selectedCategory;
        
        return matchesSearch && matchesCategory;
      });

  console.log('Rendering PendingExpenseApprovals:', {
    contractId,
    expensesLength: expenses.length,
    filteredExpensesLength: filteredExpenses.length,
    expenses,
    filteredExpenses,
    searchQuery,
    selectedCategory,
    isLoading
  });

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-yellow-600" />
            Pending Expense Approvals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between p-4 border rounded-md">
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                  <div className="h-3 bg-muted rounded animate-pulse w-1/2" />
                </div>
                <div className="h-8 w-20 bg-muted rounded animate-pulse" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-600" />
              <CardTitle>Pending Expense Approvals</CardTitle>
            </div>
            {expenses.length > 0 && (
              <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                {expenses.length} pending
              </Badge>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={fetchPendingExpenses}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {expenses.length > 0 && (
          <div className="text-sm text-muted-foreground">
            Total value: {formatAmount(getTotalPendingAmount().toString())}
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Search and Filters */}
        <div className="flex gap-2 flex-col sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search expenses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Filter className="h-4 w-4 mr-2" />
                {selectedCategory ? getCategoryInfo(selectedCategory).label : 'All Categories'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => setSelectedCategory('')}>
                All Categories
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {Object.values(ExpenseCategory).map((category) => {
                const categoryInfo = getCategoryInfo(category);
                return (
                  <DropdownMenuItem
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                  >
                    <span className="mr-2">{categoryInfo.icon}</span>
                    {categoryInfo.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Expenses Table */}
        {filteredExpenses.length > 0 ? (
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Expense</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Contractor</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExpenses.map((expense) => {
                  const categoryInfo = getCategoryInfo(expense.category);
                  return (
                    <TableRow key={expense.id} className="group" data-testid={`expense-approval-${expense.id}`}>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">{expense.description}</div>
                          <div className="text-sm text-muted-foreground">
                            ID: {expense.id}
                          </div>
                          {expense.submittedAt && (
                            <div className="text-xs text-muted-foreground">
                              Submitted {formatDate(expense.submittedAt)}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span>{categoryInfo.icon}</span>
                          <span className="text-sm">{categoryInfo.label}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {formatDate(expense.expenseDate)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {formatAmount(expense.amount)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {expense.submittedByEmail || 'Unknown'}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <TooltipProvider>
                            {onViewExpense && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => onViewExpense(expense)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>View details</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            
                            {expense.receiptUrl && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleViewReceipt(expense)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>View receipt</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleQuickApprove(expense)}
                                  className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
                                  data-testid={`approve-expense-${expense.id}`}
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Approve</p>
                              </TooltipContent>
                            </Tooltip>
                            
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleQuickReject(expense)}
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Reject</p>
                              </TooltipContent>
                            </Tooltip>

                            <DropdownMenu>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>More actions</p>
                                </TooltipContent>
                              </Tooltip>
                              <DropdownMenuContent align="end">
                                {onViewExpense && (
                                  <DropdownMenuItem onClick={() => onViewExpense(expense)}>
                                    <Eye className="mr-2 h-4 w-4" />
                                    View Details
                                  </DropdownMenuItem>
                                )}
                                {onApproveExpense && (
                                  <DropdownMenuItem onClick={() => onApproveExpense(expense)}>
                                    <Check className="mr-2 h-4 w-4" />
                                    Approve with Comments
                                  </DropdownMenuItem>
                                )}
                                {onRejectExpense && (
                                  <DropdownMenuItem onClick={() => onRejectExpense(expense)}>
                                    <X className="mr-2 h-4 w-4" />
                                    Reject with Reason
                                  </DropdownMenuItem>
                                )}
                                {expense.receiptUrl && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleViewReceipt(expense)}>
                                      <Download className="mr-2 h-4 w-4" />
                                      View Receipt
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center">
              {filteredExpenses.length === 0 && expenses.length > 0 ? (
                <Search className="h-6 w-6 text-yellow-600" />
              ) : (
                <Clock className="h-6 w-6 text-yellow-600" />
              )}
            </div>
            <h3 className="text-lg font-medium mb-1">
              {filteredExpenses.length === 0 && expenses.length > 0 
                ? 'No matching expenses'
                : 'No pending approvals'
              }
            </h3>
            <p className="text-muted-foreground mb-4">
              {filteredExpenses.length === 0 && expenses.length > 0
                ? 'Try adjusting your search or filter criteria'
                : 'All expenses have been reviewed'
              }
            </p>
            {filteredExpenses.length === 0 && expenses.length > 0 && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('');
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PendingExpenseApprovals; 